import Typography from '@components/common/typography';
import styles from './styles.module.scss';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import Button from '@components/common/button';
import { routes } from '@const/routes';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { useLocales } from '@hooks/helpers/useLocales';
import { Icon } from '@components/icons';
import { copyStringToClipboard, formatDate } from '@utils/index';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiApiKeysService } from '@api';
import { useUserContext } from '@hooks/context/useUserContext';
import Skeleton from '@components/common/skeleton';
import { useSettingsModals } from '@hooks/modals/useSettingsModals';
import { useEffect } from 'react';
import { EUserType } from '@xeppt/xeppt-sdk/types';
import { useNotFound } from '@hooks/helpers/useNotFound';

const SettingApiKeysLayout = () => {
    const { handleBack } = useNavigationContext();
    const { submitLocale } = useLocales();
    const {
        handleCreateApiKey,
        handleDeleteApiKey,
        handleUpdateWebhooks,
        handleAddWebhooks,
        handleDeleteWebhook
    } = useSettingsModals();
    const { user } = useUserContext();
    const { handleNotFound } = useNotFound();

    const {
        data: apiKeys,
        isLoading: isApiKeysLoading,
        handleRequest: refetchApiKeys
    } = useApiQuery({
        method: () => apiApiKeysService.getApiKeys(),
        deps: [user],
        condition: !!user
    });

    useEffect(() => {
        if (user && user?.type !== EUserType.BUSINESS) {
            handleNotFound({ error: { statusCode: 404 }, isRedirectNotFound: true });
        }
    }, [user]);

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={() => handleBack(routes.settings)}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">REST API key</Typography>
            </div>
            <div className={styles.content}>
                <Typography>
                    Use this secret key to authenticate API requests and validate webhook signatures
                </Typography>
                {isApiKeysLoading ? (
                    <div className={styles.api_keys_wrapper}>
                        <Skeleton className={styles.skeleton} />
                        <Skeleton className={styles.skeleton} />
                    </div>
                ) : (
                    <div className={styles.api_keys_wrapper}>
                        <div className={styles.container}>
                            <div className={styles.header}>
                                <div className={styles.left}>
                                    <div className={styles.icon}>
                                        <Icon name="key" />
                                    </div>
                                    <div className={styles.text_wrapper}>
                                        <Typography
                                            className={styles.title}
                                            variant="body1"
                                            lh="120%"
                                            weight="semibold">
                                            REST API key
                                        </Typography>
                                        <p className={styles.description}>
                                            You can create a new API key or delete an existing one
                                            here
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    variant="gold"
                                    size="small"
                                    onClick={() =>
                                        handleCreateApiKey(refetchApiKeys, apiKeys || undefined)
                                    }>
                                    Create new key
                                </Button>
                            </div>
                            <div className={styles.content}>
                                {apiKeys?.access_key ? (
                                    <>
                                        <div className={styles.row}>
                                            <div className={styles.data_wrapper}>
                                                <p className={styles.label}>Access key</p>
                                                <p className={styles.value}>
                                                    {apiKeys?.access_key}{' '}
                                                    <Icon
                                                        onClick={() => copyStringToClipboard('')}
                                                        name="copy"
                                                    />
                                                </p>
                                            </div>
                                            <div className={styles.data_wrapper}>
                                                <p className={styles.label}>Secret key</p>
                                                <p className={styles.value}>
                                                    •••••••••••••••••••••••••••••••••
                                                </p>
                                            </div>
                                        </div>
                                        <div className={styles.row}>
                                            <div className={styles.data_wrapper}>
                                                <p className={styles.label}>
                                                    Created at{' '}
                                                    {formatDate(apiKeys?.created_at, 'MMM D, YYYY')}
                                                </p>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className={styles.row}>
                                        <div className={styles.data_wrapper}>
                                            <p className={styles.label}>No API key created yet</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {apiKeys?.access_key && (
                                <Button
                                    variant="outlined-red"
                                    size="small"
                                    onClick={() => handleDeleteApiKey(refetchApiKeys)}>
                                    Delete key
                                </Button>
                            )}
                            <Button variant="text-gold" size="small">
                                View REST API documentation <Icon name="external_link" />
                            </Button>
                        </div>
                        <div className={styles.container}>
                            <div className={styles.header}>
                                <div className={styles.left}>
                                    <div className={styles.icon}>
                                        <Icon name="key" />
                                    </div>
                                    <div className={styles.text_wrapper}>
                                        <Typography
                                            className={styles.title}
                                            variant="body1"
                                            lh="120%"
                                            weight="semibold">
                                            Webhooks
                                        </Typography>
                                        <p className={styles.description}>
                                            Add and manage webhook endpoints to receive event
                                            notification
                                        </p>
                                    </div>
                                </div>
                                {apiKeys?.access_key && (
                                    <Button
                                        variant="gold"
                                        size="small"
                                        onClick={() => {
                                            if (apiKeys?.order_webhook || apiKeys?.refund_webhook) {
                                                handleUpdateWebhooks(apiKeys, refetchApiKeys);
                                            } else {
                                                handleAddWebhooks(refetchApiKeys);
                                            }
                                        }}>
                                        {apiKeys?.order_webhook || apiKeys?.refund_webhook
                                            ? 'Update webhooks'
                                            : 'Add webhooks'}
                                    </Button>
                                )}
                            </div>
                            <div className={`${styles.content} ${styles.webhooks_content}`}>
                                {apiKeys?.order_webhook || apiKeys?.refund_webhook ? (
                                    <>
                                        {apiKeys?.order_webhook && (
                                            <div className={styles.row}>
                                                <div className={styles.data_wrapper}>
                                                    <p className={styles.label}>Order webhook</p>
                                                    <p className={styles.value}>
                                                        {apiKeys?.order_webhook}
                                                        <div className={styles.webhook_actions}>
                                                            <Icon
                                                                name="copy"
                                                                onClick={() =>
                                                                    copyStringToClipboard(
                                                                        apiKeys.order_webhook || ''
                                                                    )
                                                                }
                                                            />
                                                            <Icon
                                                                name="trash"
                                                                onClick={() =>
                                                                    handleDeleteWebhook(
                                                                        apiKeys,
                                                                        'order_webhook',
                                                                        refetchApiKeys
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                        {apiKeys?.refund_webhook && (
                                            <div className={styles.row}>
                                                <div className={styles.data_wrapper}>
                                                    <p className={styles.label}>
                                                        Refund webhook
                                                        <p className={styles.value}>
                                                            {apiKeys.refund_webhook}
                                                            <div className={styles.webhook_actions}>
                                                                <Icon
                                                                    name="copy"
                                                                    onClick={() =>
                                                                        copyStringToClipboard(
                                                                            apiKeys.refund_webhook ||
                                                                                ''
                                                                        )
                                                                    }
                                                                />
                                                                <Icon
                                                                    name="trash"
                                                                    onClick={() =>
                                                                        handleDeleteWebhook(
                                                                            apiKeys,
                                                                            'refund_webhook',
                                                                            refetchApiKeys
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                        </p>
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className={styles.row}>
                                        <div className={styles.data_wrapper}>
                                            <p className={styles.label}>No webhooks added yet</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default SettingApiKeysLayout;
